import { openContextModal } from '@mantine/modals';
import { debounce, isString, unique } from 'radash';
import { batch } from 'valtio-reactive';

import { defaultDiagram } from '@diagram/data/default-diagram';
import { DiagramService } from '@diagram/services/DiagramService';
import { diagram } from '@diagram/state/diagram';
import { SideEffects } from '@diagram/state/effects/diagram';
import { historyState } from '@diagram/state/history';
import { syncingState } from '@diagram/state/syncing';

import type { Pointer, Transaction } from 'sync-engine';
import { SyncEngineClient } from 'sync-engine';
import { Diagram, DiagramSchema, WebSocketEvent } from 'models';
import { CompressionHelpers } from 'helpers/CompressionHelpers';
import { SyncEngineHelpers } from 'helpers/SyncEngineHelpers';

import { ProjectDiagramService } from 'services/ProjectDiagramService';
import { RealtimeService } from 'services/RealtimeService';
import { permissionsState } from 'state/current-project-permissions';

import { DiagramAutofixService } from './DiagramAutofixService';

const DiagramSyncService = {
    batching: false,

    syncedTimeout: null as NodeJS.Timeout | null,

    autoSaveErrorCount: 0,

    client: new SyncEngineClient<Diagram>({
        setState: (state) => {
            batch(() => {
                Object.entries(state).forEach(([key, value]) => {
                    SyncEngineHelpers.set(diagram, key, value);
                });

                Object.keys(diagram).forEach((key) => {
                    if (!(key in state)) {
                        SyncEngineHelpers.unset(diagram, key);
                    }
                });
            });
        },
        getState: () => diagram,
        snapshot: defaultDiagram(),
        onTransaction: () => {
            if (process.env.TEST) {
                return;
            }

            DiagramSyncService.debouncedSync();
        },
        validateState: (state) => {
            const validator = DiagramSchema.safeParse(state);

            if (validator.success) {
                return {
                    success: true,
                    error: false,
                    errors: [],
                };
            }

            if (process.env.TEST) {
                throw new Error(validator.error.message);
            } else {
                openContextModal({
                    modal: 'infoModal',
                    title: 'Sorry, something went wrong',
                    innerProps: {
                        children: `
                            Your last action wasn't saved due to a system error.
                            The team has been notified and we're working on a fix.

                            Please try again later.
                        `,
                    },
                });
            }

            console.error('Error validating diagram state', validator.error);

            return {
                success: validator.success,
                error: !validator.success,
                errors: [validator.error.message],
            };
        },
        broadcastTransaction: async (transaction) => {
            const clients = RealtimeService.clients;

            if (process.env.TEST || !DiagramSyncService.allowSyncing() || !clients) {
                return;
            }

            const channel = clients.ably.channels.get(`diagram-${diagram.id}`);
            const transactionAsString = JSON.stringify(transaction);
            const transactionSize = CompressionHelpers.calculateStringSize(transactionAsString);

            channel
                .publish(
                    WebSocketEvent.DIAGRAM_TRANSACTION,
                    transactionSize > 60_000
                        ? await CompressionHelpers.compress(transactionAsString)
                        : {
                              transaction,
                          },
                )
                .catch((error) => {
                    console.warn({
                        message: 'Error broadcasting transaction',
                        error,
                    });
                })
                .then();
        },
        updateUndoRedoCount: (undoRedoCount) => {
            if (process.env.TEST) {
                return;
            }

            historyState.undoCount = undoRedoCount.undoCount;
            historyState.redoCount = undoRedoCount.redoCount;
        },
    }),

    allowSyncing: () => {
        return !!diagram.id && !!permissionsState.permissions['elements.edit'];
    },

    save: (autofix = true) => {
        if (DiagramSyncService.batching) return;

        // Run effects
        const changes = DiagramSyncService.client.getChanges();

        const effects = [
            // Disable this for now, it's breaking copy pasting specifications and stuff.
            // SideEffects.syncComponentGenericVoltageTypes,
            SideEffects.syncTwoPortConverterPower,
            // SideEffects.syncTwoPortComponentPowerFlowConfiguration,
            SideEffects.cleanupFaultFeedingPortsOnDelete,
            SideEffects.syncConnectionInstallationMethod,
        ];

        changes.forEach((change) => effects.forEach((effect) => effect(change)));

        const parsed = DiagramSchema.safeParse(diagram);

        if (parsed.success) {
            DiagramSyncService.client.save();
        } else {
            if (autofix) {
                DiagramAutofixService.autofix();
                DiagramSyncService.save(false);

                return;
            }

            console.error(
                'Failed to validate diagram when saving',
                JSON.stringify(parsed.error?.issues, null, 2),
                JSON.stringify(diagram, null, 2),
            );

            DiagramService.openForceRefreshModal('saving');
        }
    },

    create: (key: string, value: any, apply: boolean) => {
        DiagramSyncService.client.create({ [key]: value }, apply);
    },

    updateByKey: (key: string, value: any, apply: boolean, previousValue: any = undefined) => {
        if (key.includes('..')) {
            console.error('Invalid key with a missing property', key, value, previousValue);
            return;
        }

        if (apply) {
            SyncEngineHelpers.set(diagram, key, value);
        } else {
            const replaceIndexById = (key: string) => {
                return key.replace(/\.(\d+)\./g, (match, indexStr, offset) => {
                    const index = parseInt(indexStr, 10);
                    const arrayPath = key.substring(0, offset);
                    const array = SyncEngineHelpers.get(diagram, arrayPath);
                    const id = array?.[index]?.id;
                    return id ? `.${id}.` : match;
                });
            };

            const updatedKey = replaceIndexById(key);
            const previousData = previousValue !== undefined ? { [updatedKey]: previousValue } : undefined;

            DiagramSyncService.client.update({ [updatedKey]: value }, false, previousData);
        }
    },

    update: (data: { [key: string]: any }) => {
        // Discard empty data
        if (Object.keys(data).length === 0) {
            return;
        }

        Object.entries(data).forEach(([key, value]) => {
            SyncEngineHelpers.set(diagram, key, value);
        });
    },

    delete: (key: string | string[], apply: boolean, previousValue: any = undefined) => {
        const data = isString(key) ? [key] : key;

        // Discard empty data
        if (data.length === 0) {
            return;
        }

        const previousData = isString(key) && previousValue ? { [key]: previousValue } : undefined;

        DiagramSyncService.client.delete(unique(data), apply, previousData);
    },

    sync: async () => {
        if (!DiagramSyncService.allowSyncing()) {
            return;
        }

        try {
            if (syncingState.status === 'syncing') {
                // Don't start a new sync when the previous one is still going.
                return;
            }

            if (DiagramSyncService.syncedTimeout) {
                clearTimeout(DiagramSyncService.syncedTimeout);
            }

            const transactions = DiagramSyncService.client.pushToServer();

            if (transactions.length === 0) {
                return;
            }

            const transactionsAsString = JSON.stringify(transactions);
            const transactionsSize = CompressionHelpers.calculateStringSize(transactionsAsString);

            console.log('TRANSACTIONS', transactions);

            if (transactionsSize > 60_000) {
                console.warn('BIG TRANSACTION', transactions);
            }

            syncingState.status = 'syncing';
            syncingState.error = '';

            const response = await ProjectDiagramService.sync(
                diagram.id,
                transactions,
                DiagramSyncService.client.pointer,
            );

            if (response?.transactions) {
                DiagramSyncService.client.pullFromServer(response.transactions);
            }

            DiagramSyncService.autoSaveErrorCount = 0;
            syncingState.status = 'synced';

            const transactionsInQueue = DiagramSyncService.client.pushToServer();

            if (transactionsInQueue.length === 0) {
                DiagramSyncService.syncedTimeout = setTimeout(() => {
                    if (syncingState.status === 'synced') {
                        syncingState.status = null;
                    }
                }, 1000);
            } else {
                await DiagramSyncService.sync();
            }
        } catch (error) {
            // Todo: maybe refresh on sync error?
            DiagramSyncService.autoSaveErrorCount += 1;

            if (DiagramSyncService.autoSaveErrorCount >= 3) {
                DiagramService.openForceRefreshModal('saving');
            }

            console.log({
                title: 'Sync failed',
                message: `Error: ${error}`,
            });

            syncingState.status = 'error';
            syncingState.error = `${error}`;
        }
    },

    debouncedSync: debounce({ delay: 2500 }, () => {
        DiagramSyncService.sync().then();
    }),

    pullTransactions: (transactions: Transaction[]) => {
        DiagramSyncService.client.pull(transactions);
    },

    get: (): Diagram => diagram,

    initialize: (diagram: Diagram, pointer: Pointer | undefined) => {
        const defaultPointer: Pointer = {
            id: '',
            date: '',
        };

        DiagramSyncService.client.refresh(diagram, pointer ?? defaultPointer);
    },

    undo: () => batch(() => DiagramSyncService.client.undo()),

    redo: () => batch(() => DiagramSyncService.client.redo()),
};

export { DiagramSyncService };
